import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/admin/AdminProjectForm.css';

const AdminProjectEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    excerpt: '',
    content: '',
    category: 'web-development',
    status: 'draft',
    priority: 'medium',
    featured: false,
    client_name: '',
    client_email: '',
    client_phone: '',
    location: '',
    start_date: '',
    end_date: '',
    completion_date: '',
    budget: '',
    progress: 0,
    technologies: '',
    team_members: '',
    notes: '',
    meta_title: '',
    meta_description: ''
  });
  const [currentImage, setCurrentImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentGallery, setCurrentGallery] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [previewGallery, setPreviewGallery] = useState([]);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const categories = [
    { value: 'web-development', label: 'Phát triển Web' },
    { value: 'mobile-app', label: 'Ứng dụng di động' },
    { value: 'architecture', label: 'Kiến trúc xây dựng' },
    { value: 'landscape', label: 'Tiểu cảnh sân vườn' },
    { value: 'solar', label: 'Năng lượng mặt trời' },
    { value: 'iot-cctv', label: 'IoT & Camera' }
  ];

  const statuses = [
    { value: 'draft', label: 'Nháp' },
    { value: 'published', label: 'Đã xuất bản' },
    { value: 'archived', label: 'Lưu trữ' },
    { value: 'planning', label: 'Lên kế hoạch' },
    { value: 'in-progress', label: 'Đang thực hiện' },
    { value: 'completed', label: 'Hoàn thành' },
    { value: 'on-hold', label: 'Tạm dừng' }
  ];

  const priorities = [
    { value: 'low', label: 'Thấp' },
    { value: 'medium', label: 'Trung bình' },
    { value: 'high', label: 'Cao' },
    { value: 'urgent', label: 'Khẩn cấp' }
  ];

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoadingProject(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const project = data.data;
        
        setFormData({
          title: project.title || '',
          description: project.description || '',
          excerpt: project.excerpt || '',
          content: project.content || '',
          category: project.category || 'web-development',
          status: project.status || 'draft',
          priority: project.priority || 'medium',
          featured: Boolean(project.featured),
          client_name: project.client_name || '',
          client_email: project.client_email || '',
          client_phone: project.client_phone || '',
          location: project.location || '',
          start_date: project.start_date ? project.start_date.split('T')[0] : '',
          end_date: project.end_date ? project.end_date.split('T')[0] : '',
          completion_date: project.completionDate ? project.completionDate.split('T')[0] : '',
          budget: project.budget || '',
          progress: project.progress || 0,
          technologies: project.technologies || '',
          team_members: project.team_members || '',
          notes: project.notes || '',
          meta_title: project.meta_title || '',
          meta_description: project.meta_description || ''
        });
        if (project.image) {
          setCurrentImage(`${API_BASE_URL}${project.image}`);
        }
        
        // Load current gallery
        if (project.gallery) {
          try {
            const galleryPaths = typeof project.gallery === 'string' 
              ? JSON.parse(project.gallery) 
              : project.gallery;
            if (Array.isArray(galleryPaths)) {
              const fullPaths = galleryPaths.map(path => `${API_BASE_URL}${path}`);
              setCurrentGallery(fullPaths);
            }
          } catch (error) {
            console.error('Error parsing gallery:', error);
          }
        }
      } else {
        throw new Error(data.error || 'Không thể tải dự án');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      alert('Lỗi: ' + error.message);
      navigate('/admin/projects');
    } finally {
      setLoadingProject(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile(file);
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles(files);
    
    // Create preview for gallery images
    const previews = [];
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews[index] = e.target.result;
        if (previews.length === files.length) {
          setPreviewGallery([...previews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    const newFiles = galleryFiles.filter((_, i) => i !== index);
    const newPreviews = previewGallery.filter((_, i) => i !== index);
    setGalleryFiles(newFiles);
    setPreviewGallery(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Vui lòng nhập tiêu đề dự án');
      return;
    }

    if (formData.client_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)) {
      alert('Email khách hàng không hợp lệ');
      return;
    }

    if (formData.client_phone && !/^[0-9+\-\s()]{10,15}$/.test(formData.client_phone)) {
      alert('Số điện thoại khách hàng không hợp lệ');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Prepare form data for submission
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'progress') {
            submitData.append(key, parseInt(formData[key]) || 0);
          } else if (key === 'budget') {
            submitData.append(key, parseFloat(formData[key]) || 0);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      // Add image file if selected
      if (selectedFile) {
        submitData.append('image', selectedFile);
      }

      // Add gallery files if selected
      if (galleryFiles.length > 0) {
        galleryFiles.forEach((file) => {
          submitData.append('gallery', file);
        });
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, browser will set it automatically
        },
        body: submitData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('Cập nhật dự án thành công!');
        navigate('/admin/projects');
      } else {
        throw new Error(data.error || 'Lỗi khi cập nhật dự án');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingProject) {
    return (
      <div className="admin-project-form">
        <div className="loading-state">
          <div className="loading-spinner">🔄</div>
          <p>Đang tải dự án...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-project-form">
      <div className="form-header">
        <div className="header-content">
          <h1>✏️ Chỉnh Sửa Dự Án</h1>
          <p>Cập nhật thông tin dự án: {formData.title}</p>
        </div>
        <button 
          type="button" 
          onClick={() => navigate('/admin/projects')}
          className="back-btn"
        >
          ← Quay lại
        </button>
      </div>

      <form onSubmit={handleSubmit} className="project-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>📝 Thông tin cơ bản</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Tiêu đề dự án *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Nhập tiêu đề dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="excerpt">Mô tả ngắn</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="3"
                placeholder="Mô tả ngắn gọn về dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">Mô tả chi tiết</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Mô tả chi tiết về dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="content">Nội dung</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="6"
                placeholder="Nội dung đầy đủ của dự án..."
              />
            </div>
          </div>
        </div>

        {/* Project Image */}
        <div className="form-section">
          <h3>🖼️ Hình ảnh dự án</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="image">Hình ảnh đại diện</label>
              
              {/* Current Image Display */}
              {currentImage && !previewImage && (
                <div className="current-image">
                  <p>Hình ảnh hiện tại:</p>
                  <img src={currentImage} alt="Current project image" />
                </div>
              )}
              
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
              />
              
              {/* Preview New Image */}
              {previewImage && (
                <div className="image-preview">
                  <p>Hình ảnh mới:</p>
                  <img src={previewImage} alt="Preview" />
                  <button 
                    type="button" 
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewImage(null);
                    }}
                    className="remove-image-btn"
                  >
                    ✖️ Hủy thay đổi
                  </button>
                </div>
              )}
              
              <small className="form-hint">Chấp nhận: JPG, PNG, GIF. Tối đa 5MB. Để trống nếu không muốn thay đổi.</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gallery">Thư viện ảnh (Gallery)</label>
              
              {/* Current Gallery Display */}
              {currentGallery.length > 0 && previewGallery.length === 0 && (
                <div className="current-gallery">
                  <p>Thư viện ảnh hiện tại ({currentGallery.length} ảnh):</p>
                  <div className="gallery-grid">
                    {currentGallery.map((imagePath, index) => (
                      <div key={index} className="gallery-item current">
                        <img src={imagePath} alt={`Current gallery ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <input
                type="file"
                id="gallery"
                name="gallery"
                onChange={handleGalleryChange}
                accept="image/*"
                multiple
                className="file-input"
              />
              
              {/* Preview New Gallery */}
              {previewGallery.length > 0 && (
                <div className="gallery-preview">
                  <p>Thư viện ảnh mới ({previewGallery.length} ảnh):</p>
                  <div className="gallery-grid">
                    {previewGallery.map((preview, index) => (
                      <div key={index} className="gallery-item">
                        <img src={preview} alt={`New gallery ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="remove-gallery-btn"
                        >
                          ✖️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <small className="form-hint">Chọn nhiều ảnh để thay thế thư viện hiện tại. Chấp nhận: JPG, PNG, GIF. Tối đa 5MB mỗi ảnh. Để trống nếu không muốn thay đổi.</small>
            </div>
          </div>
        </div>

        {/* Project Settings */}
        <div className="form-section">
          <h3>⚙️ Cài đặt dự án</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Danh mục</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Trạng thái</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Ưu tiên</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="progress">Tiến độ (%)</label>
              <input
                type="number"
                id="progress"
                name="progress"
                value={formData.progress}
                onChange={handleInputChange}
                min="0"
                max="100"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="featured">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                />
                Dự án nổi bật
              </label>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/projects')}
            className="cancel-btn"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
          >
            {loading ? '🔄 Đang cập nhật...' : '✅ Cập nhật dự án'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjectEdit; 